import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSync, MdTimeline, MdCheckCircle, MdError, MdSchedule, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { tradingService } from '../../services/tradingService';
import { useAuth } from '../../context/AuthContext';
import MetricCard from '../../components/shared/MetricCard';

const DashboardPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [data, setData] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncOpen, setSyncOpen] = useState(true);
  const [positionsOpen, setPositionsOpen] = useState(true);
  const [ordersOpen, setOrdersOpen] = useState(true);
  const navigate = useNavigate();

  const loadAll = useCallback(() => {
    Promise.all([
      tradingService.getDashboard().then(r => setData(r.data)).catch(() => {}),
      tradingService.getSyncStatus().then(r => setSyncLogs(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSync = async (mode) => {
    setSyncing(true);
    try {
      await tradingService.triggerSync(null, mode);
    } catch (_) {
      // silent
    } finally {
      setSyncing(false);
      setTimeout(loadAll, 2000);
    }
  };

  const formatDate = (d) => {
    if (!d) return '--';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) +
      ' ' + dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const statusIcon = (st) => {
    if (st === 'success') return <MdCheckCircle className="text-green-500" size={14} />;
    if (st === 'failed') return <MdError className="text-red-500" size={14} />;
    if (st === 'running') return <MdSync className="animate-spin text-brand-gold" size={14} />;
    return <MdSchedule className="text-gray-400" size={14} />;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-brand-gold" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading trading dashboard...</p>
      </div>
    );
  }

  const cardCls = 'bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-serif font-semibold text-gray-800 dark:text-gray-200">TRADING DASHBOARD</h2>
      </div>

      {/* Metric tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <MetricCard
          label="ACTIVE STRATEGIES"
          value={data?.active_strategies || 0}
          subtitle={`of ${data?.total_strategies || 0} total`}
        />
        <MetricCard
          label="SYMBOLS TRACKED"
          value={data?.symbols_tracked || 0}
        />
        <MetricCard
          label="OPEN POSITIONS"
          value={data?.open_positions?.length || 0}
        />
        <MetricCard
          label="UNREALIZED P&L"
          value={data?.total_unrealized_pnl != null ? `$${Number(data.total_unrealized_pnl).toFixed(2)}` : '$0.00'}
          valueColor={
            data?.total_unrealized_pnl > 0
              ? 'text-green-600 dark:text-green-400'
              : data?.total_unrealized_pnl < 0
                ? 'text-red-500 dark:text-red-400'
                : undefined
          }
        />
      </div>

      {/* Quick Actions + Sync Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Quick Actions */}
        <div className={`${cardCls} p-4 md:p-5`}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">QUICK ACTIONS</h3>
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <button
                onClick={() => handleSync('daily_eod')}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 text-xs rounded-lg bg-brand-gold text-white hover:bg-brand-gold-dark disabled:opacity-40 font-medium"
              >
                <MdSync className={syncing ? 'animate-spin' : ''} size={14} />
                {syncing ? 'Syncing...' : 'Sync EOD Data'}
              </button>
            )}
            <button
              onClick={() => navigate('/trading/backtest')}
              className="flex items-center gap-2 px-4 py-2 text-xs rounded-lg border border-brand-gold text-brand-gold hover:bg-brand-gold/10 font-medium"
            >
              <MdTimeline size={14} />
              Run Backtest
            </button>
            {isAdmin && (
              <button
                onClick={() => handleSync('bulk_backfill')}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 disabled:opacity-40 font-medium"
              >
                Bulk Backfill
              </button>
            )}
          </div>
        </div>

        {/* Sync Status — admin only */}
        {isAdmin && <div className={`${cardCls} p-4 md:p-5`}>
          <button
            onClick={() => setSyncOpen(!syncOpen)}
            className="flex items-center justify-between w-full mb-2"
          >
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">DATA SYNC STATUS</h3>
            {syncOpen ? <MdExpandLess className="text-gray-400" /> : <MdExpandMore className="text-gray-400" />}
          </button>
          {syncOpen && (
            <div className="max-h-48 overflow-y-auto space-y-1.5">
              {syncLogs.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No sync logs</p>
              ) : (
                syncLogs.slice(0, 8).map((log, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      {statusIcon(log.status)}
                      <span className="text-gray-700 dark:text-gray-300 truncate">{log.sync_type}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-gray-500 tabular-nums">{log.records_processed} rec</span>
                      <span className="text-gray-400 tabular-nums">{formatDate(log.finished_at || log.started_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>}
      </div>

      {/* Open Positions */}
      <div className={`${cardCls} p-4 md:p-5 mb-4`}>
        <button
          onClick={() => setPositionsOpen(!positionsOpen)}
          className="flex items-center justify-between w-full mb-2"
        >
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            OPEN POSITIONS ({data?.open_positions?.length || 0})
          </h3>
          {positionsOpen ? <MdExpandLess className="text-gray-400" /> : <MdExpandMore className="text-gray-400" />}
        </button>
        {positionsOpen && (
          data?.open_positions?.length > 0 ? (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50 text-[10px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-3 py-2.5 text-left">Symbol</th>
                    <th className="px-3 py-2.5 text-right">Qty</th>
                    <th className="px-3 py-2.5 text-right">Entry</th>
                    <th className="px-3 py-2.5 text-right">Current</th>
                    <th className="px-3 py-2.5 text-right">P&L</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.open_positions.map((p, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{p.symbol_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">{p.quantity}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">${p.avg_entry_price}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">${p.current_price || '--'}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                        p.unrealized_pnl > 0 ? 'text-green-600 dark:text-green-400' : p.unrealized_pnl < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-500'
                      }`}>${Number(p.unrealized_pnl || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No open positions</p>
          )
        )}
      </div>

      {/* Recent Orders */}
      <div className={`${cardCls} p-4 md:p-5`}>
        <button
          onClick={() => setOrdersOpen(!ordersOpen)}
          className="flex items-center justify-between w-full mb-2"
        >
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            RECENT ORDERS ({data?.recent_orders?.length || 0})
          </h3>
          {ordersOpen ? <MdExpandLess className="text-gray-400" /> : <MdExpandMore className="text-gray-400" />}
        </button>
        {ordersOpen && (
          data?.recent_orders?.length > 0 ? (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50 text-[10px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-3 py-2.5 text-left">Symbol</th>
                    <th className="px-3 py-2.5 text-left">Action</th>
                    <th className="px-3 py-2.5 text-right">Qty</th>
                    <th className="px-3 py-2.5 text-right">Fill Price</th>
                    <th className="px-3 py-2.5 text-left">Status</th>
                    <th className="px-3 py-2.5 text-left hidden sm:table-cell">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.recent_orders.map((o, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{o.symbol_name}</td>
                      <td className={`px-3 py-2 font-semibold text-xs ${o.action === 'BUY' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>{o.action}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">{o.quantity}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">{o.fill_price ? `$${o.fill_price}` : '--'}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-medium ${
                          o.status === 'filled' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          o.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>{o.status}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 hidden sm:table-cell">{formatDate(o.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No orders yet</p>
          )
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
